import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { paSegments } from "@/lib/api";
import { MetricTable, num } from "./paShared";

const NAME_BY_ID = { TLC: "North East", TLD: "North West", TLE: "Yorkshire and The Humber", TLF: "East Midlands", TLG: "West Midlands", TLH: "East of England", TLI: "London", TLJ: "South East", TLK: "South West", TLL: "Wales", TLM: "Scotland", TLN: "Northern Ireland" };

function colour(pct, lo, hi) {
  if (pct == null) return "#e2e8f0";
  const t = hi > lo ? Math.min(1, Math.max(0, (pct - lo) / (hi - lo))) : 0.5;
  const r = Math.round(16 + (190 - 16) * t), g = Math.round(150 + (24 - 150) * t), b = Math.round(110 + (60 - 110) * t);
  return `rgb(${r},${g},${b})`;
}

export function UkMap({ rows }) {
  const [topo, setTopo] = useState(null);
  const [hover, setHover] = useState(null);
  useEffect(() => { fetch("/uk_itl1.json").then((r) => r.json()).then(setTopo).catch(() => setTopo(false)); }, []);
  const byName = useMemo(() => Object.fromEntries((rows || []).map((r) => [r.group, r])), [rows]);
  const geo = useMemo(() => (topo ? feature(topo, topo.objects.geog) : null), [topo]);
  const W = 420, H = 560;
  const path = useMemo(() => (geo ? geoPath(geoMercator().fitSize([W, H], geo)) : null), [geo]);
  if (topo === false) return <p className="text-xs text-rose-600">Map data could not be loaded.</p>;
  if (!geo) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  const pcts = (rows || []).filter((r) => !r.too_few && r.bad_pct != null).map((r) => r.bad_pct);
  const lo = pcts.length ? Math.min(...pcts) : 0, hi = pcts.length ? Math.max(...pcts) : 1;
  return (
    <div className="relative" data-testid="pa-geo-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[420px] h-auto">
        {geo.features.map((f) => {
          const name = NAME_BY_ID[f.properties.id] || f.properties.name;
          const r = byName[name];
          const fill = r && !r.too_few ? colour(r.bad_pct, lo, hi) : r ? "#cbd5e1" : "#f1f5f9";
          return <path key={f.properties.id} d={path(f)} fill={fill} stroke="#fff" strokeWidth={1} className="transition-opacity hover:opacity-80 cursor-pointer" onMouseEnter={() => setHover({ name, r })} onMouseLeave={() => setHover(null)} data-testid={`pa-geo-region-${f.properties.id}`} />;
        })}
      </svg>
      <div className="absolute top-2 left-2 rounded bg-white/90 border px-2 py-1 text-[11px] shadow-sm min-w-[190px]" data-testid="pa-geo-tooltip">
        {hover ? (
          <>
            <p className="font-semibold">{hover.name}</p>
            {hover.r ? <p>{num(hover.r.loans)} loans · bad {hover.r.too_few ? "too few to judge" : `${hover.r.bad_pct}%`}{hover.r.bad_ci95 && !hover.r.too_few ? ` (${hover.r.bad_ci95[0]}–${hover.r.bad_ci95[1]}%)` : ""}</p> : <p className="text-slate-500">no loans</p>}
          </>
        ) : <p className="text-slate-500">Hover a region · colour = bad % (green low → red high); grey = too few to judge</p>}
      </div>
      <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-1"><span>{lo}%</span><span className="h-2 w-28 rounded" style={{ background: `linear-gradient(90deg, ${colour(lo, lo, hi)}, ${colour(hi, lo, hi)})` }} /><span>{hi}%</span></div>
    </div>
  );
}

export default function PaGeography({ region }) {
  const [maturedOnly, setMaturedOnly] = useState(true);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); paSegments(region, maturedOnly).then(setD); }, [region, maturedOnly]);
  if (region !== "UK") return <p className="text-xs text-slate-500">Location analysis is built from Companies House and covers the UK book only.</p>;
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  const seg = (k) => d.segments.find((s) => s.key === k);
  const regionSeg = seg("ch_geo_region");
  return (
    <div className="space-y-4" data-testid="pa-geography">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-600">Location = Companies House registered office postcode (as on file today), grouped by nation, ITL1 region and postcode area. {d.ch_note}</p>
        <label className="flex items-center gap-2 text-xs"><Switch checked={maturedOnly} onCheckedChange={setMaturedOnly} data-testid="pa-geo-matured-toggle" /> Matured loans only</label>
      </div>
      <div className="grid lg:grid-cols-[440px_minmax(0,1fr)] gap-4 items-start">
        <div className="rounded-lg border p-3"><p className="text-sm font-semibold mb-2 flex items-center gap-2">Bad % by region <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-300">Phase 4 · Companies House</Badge></p><UkMap rows={regionSeg?.rows || []} /></div>
        <div className="space-y-4 min-w-0">
          {["ch_geo_nation", "ch_geo_region"].map((k) => seg(k) && (
            <div key={k}><p className="text-sm font-semibold mb-1">{seg(k).label}</p><MetricTable rows={seg(k).rows} total={seg(k).total || d.total} region={region} firstCol={seg(k).label} testid={`pa-segment-${k}`} showEarlyBad /></div>
          ))}
        </div>
      </div>
      {seg("ch_geo_area") && <div><p className="text-sm font-semibold mb-1">{seg("ch_geo_area").label}</p><MetricTable rows={seg("ch_geo_area").rows} total={seg("ch_geo_area").total || d.total} region={region} firstCol="Postcode area" testid="pa-segment-ch_geo_area" showEarlyBad /></div>}
      <p className="text-[11px] text-slate-500">Bad % shows a 95% range under each figure; groups with fewer than 50 loans or 10 bad loans are greyed as "too few to judge". Registered offices that moved after funding (AD01 filed later) are flagged on the loan — the location shown is today's address. The LoanDisk trading-address region is a cross-check column in the Loans export.</p>
    </div>
  );
}
